import { useState, useMemo, useRef, useCallback } from "react";
import { RiSearchLine } from "@remixicon/react";
import { Input } from "@/components/ui/input";
import PresetCard from "./PresetCard";
import { presetItems } from "./PresetContent";
import type { PresetItem } from "./PresetContent";
import { PlatformType } from "@/types";

/** Platform-specific text overrides: { presetId: { platform: { title?, description? } } } */
const platformOverrides: Record<
  string,
  Partial<Record<PlatformType, { title?: string; description?: string }>>
> = {
  adsets: {
    [PlatformType.GOOGLE]: {
      title: "Ad group",
      description:
        "Monitor the ads based on Ad groups, ads within same ad group would be grouped together to track.",
    },
  },
};

interface SectionPresetProps {
  onSelect?: (item: PresetItem) => void;
  onSkip?: () => void;
  /** Available group_by option keys from data configurations. Non-AI presets
   *  whose group_by value isn't in this set will be hidden. */
  availableGroupByKeys?: Set<string>;
  /** Current ad account platform — used for platform-specific title overrides */
  platform?: PlatformType;
}

type FilterTab = "all" | "performance" | "ai";

const filterTabs: { id: FilterTab; label: string }[] = [
  { id: "all", label: "All" },
  { id: "performance", label: "Performance" },
  { id: "ai", label: "AI Section" },
];

const SectionPreset = ({ onSelect, onSkip, availableGroupByKeys, platform }: SectionPresetProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<FilterTab>("all");
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleFilterChange = useCallback((filter: FilterTab) => {
    setActiveFilter(filter);
    scrollRef.current?.scrollTo({ top: 0 });
  }, []);

  const filteredItems = useMemo(() => {
    let items: PresetItem[] = presetItems;

    // Apply platform-specific text overrides (e.g. "Adsets" → "Ad group" for Google)
    if (platform) {
      items = items.map((item) => {
        const override = platformOverrides[item.id]?.[platform];
        if (!override) return item;
        return {
          ...item,
          ...(override.title && { title: override.title }),
          ...(override.description && { description: override.description }),
        };
      });
    }

    // Hide non-AI presets whose group_by value isn't available for this account
    if (availableGroupByKeys) {
      items = items.filter((item) => {
        // Always keep AI presets, custom presets, and presets without a group_by
        if (item.isAI || item.isCustom) return true;
        const groupBy = item.configuration?.dropdowns?.group_by;
        if (!groupBy) return true;
        // group_by can be a string or string[] — check all values are available
        if (Array.isArray(groupBy)) {
          return groupBy.every((key) => availableGroupByKeys.has(key));
        }
        return availableGroupByKeys.has(groupBy);
      });
    }

    if (activeFilter === "ai") {
      items = items.filter((item) => item.isAI);
    } else if (activeFilter === "performance") {
      items = items.filter((item) => !item.isAI);
    }

    if (!searchQuery.trim()) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(
      (item) =>
        item.title.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query),
    );
  }, [searchQuery, activeFilter, availableGroupByKeys, platform]);

  // 280px image + 8px padding each side = 296px per card, 3 cols + 2 gaps of 12px
  const gridWidth = 296 * 3 + 12 * 2;

  return (
    <div
      className="flex flex-col min-h-0 flex-1"
      style={{
        fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
        width: gridWidth,
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 pb-[12px] mb-[12px] border-b border-[#E7E8E9] shrink-0 -mx-[12px] px-[12px]">
        <div className="flex flex-col gap-0 pl-[4px]">
          <h2 className="text-[16px] font-medium leading-[22px] text-[#35467A]">
            Create section
          </h2>
          <p className="text-[14px] font-normal leading-[20px] text-[#67749B]">
            Choose a template or start with a custom AI option
          </p>
        </div>

        <div className="flex items-center gap-[12px] shrink-0">
          {/* Search */}
          <div className="relative w-[440px]">
            <RiSearchLine
              size={14}
              className="absolute left-[10px] top-1/2 -translate-y-1/2 text-[#94A3B8]"
            />
            <Input
              placeholder="Search template"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-[32px] text-[13px] text-[#1E293B] placeholder:text-[#9AA3BD] rounded-[8px] bg-white border border-[#E7E8E9] focus:ring-0 shadow-none"
            />
          </div>

          {/* Skip button */}
          <button
            onClick={onSkip}
            className="h-[32px] px-[14px] rounded-[8px] text-[13px] font-medium text-[#67749B] hover:text-[#35467A] hover:bg-[#F1F2F4] transition-colors cursor-pointer shrink-0"
          >
            Skip
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-[6px] mb-[12px] shrink-0">
        {filterTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleFilterChange(tab.id)}
            className={`relative flex min-w-[60px] px-[12px] py-[4px] justify-center items-center gap-[6px] rounded-[6px] text-[11px] font-normal leading-[16px] cursor-pointer transition-colors border bg-white ${
              activeFilter === tab.id
                ? "border-[#9AA3BD] text-[#35467A]"
                : "border-[#E7E8E9] text-[#67749B] hover:border-[#9AA3BD]"
            }`}
          >
            {/* Invisible bold text to reserve width */}
            <span className="invisible font-medium">{tab.label}</span>
            <span
              className={`absolute ${activeFilter === tab.id ? "font-medium" : "font-normal"}`}
            >
              {tab.label}
            </span>
          </button>
        ))}
      </div>

      {/* Card Grid - fills remaining height and scrolls */}
      <div
        ref={scrollRef}
        className="overflow-y-auto flex-1 min-h-0 -mr-[12px] pr-[12px] scroll-smooth [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[#D1D5DB] [&::-webkit-scrollbar-thumb]:rounded-full"
      >
        <div className="grid grid-cols-3 gap-[12px] pb-[20px]">
          {filteredItems.map((item) => (
            <PresetCard key={item.id} item={item} onClick={onSelect} />
          ))}
        </div>

        {/* Empty state */}
        {filteredItems.length === 0 && (
          <div className="flex items-center justify-center py-[40px]">
            <p className="text-[13px] text-[#94A3B8]">
              No templates found for "{searchQuery}"
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SectionPreset;
